import React, { useRef, useEffect } from 'react';
import { Input } from './ui/input';
import { MapPin } from 'lucide-react';

const AddressAutocomplete = ({ 
  id, 
  name, 
  placeholder, 
  value, 
  onChange, 
  required = false,
  dataTestId 
}) => {
  const inputRef = useRef(null);
  const autocompleteRef = useRef(null);

  useEffect(() => {
    let listener = null;

    const initializeAutocomplete = () => {
      if (!window.google || !window.google.maps || !window.google.maps.places) {
        console.log('Google Places API not ready yet, retrying...');
        setTimeout(initializeAutocomplete, 500);
        return;
      }

      if (!inputRef.current) {
        console.log('Input ref not ready');
        return;
      }

      try {
        // Initialize Google Places Autocomplete
        autocompleteRef.current = new window.google.maps.places.Autocomplete(
          inputRef.current,
          {
            types: ['address'],
            componentRestrictions: { country: 'us' }, // Restrict to US
            fields: ['formatted_address', 'geometry', 'name']
          }
        );

        console.log('Google Places Autocomplete initialized successfully');

        // Listen for place selection
        listener = autocompleteRef.current.addListener('place_changed', () => {
          const place = autocompleteRef.current.getPlace();
          
          if (place.formatted_address) {
            // Create a synthetic event to maintain compatibility
            const syntheticEvent = {
              target: {
                name: name,
                value: place.formatted_address
              }
            };
            onChange(syntheticEvent);
          }
        });
      } catch (error) {
        console.error('Error initializing Google Places Autocomplete:', error);
      }
    };

    initializeAutocomplete();

    return () => {
      if (listener) {
        window.google.maps.event.removeListener(listener);
      }
    };
  }, [name, onChange]);

  const handleInputChange = (e) => {
    onChange(e);
  };

  return (
    <div className="flex gap-2">
      <div className="relative flex-1">
        <Input
          ref={inputRef}
          id={id}
          name={name}
          type="text"
          placeholder={placeholder}
          value={value}
          onChange={handleInputChange}
          required={required}
          className="form-input pr-10"
          data-testid={dataTestId}
          autoComplete="off"
        />
        <MapPin className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
      </div>
      {value && (
        <a
          href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(value)}`}
          target="_blank"
          rel="noopener noreferrer"
          className="px-3 py-2 border rounded-md hover:bg-gray-100 flex items-center gap-1 text-sm whitespace-nowrap"
          data-testid={`${dataTestId}-map-link`}
        >
          <MapPin className="w-4 h-4" />
          Map
        </a>
      )}
    </div>
  );
};

export default AddressAutocomplete;
